import React, { useState, useEffect, useMemo } from 'react';
import { Box, Tab, Typography, Tooltip } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import InfoIcon from '@mui/icons-material/Info';
import { useApiGet } from '../../../utils/httpClientTs';
import { getIRSFormSections } from '../../../endpoints/irsForms';
import IRSFormSection from './IRSFormSection';
import IRSFormSummary from './IRSFormSummary';
import { IRSFormDetail } from '../../../types/irsForms';

interface IRSFormSectionsProps {
  formId: string;
  formDetail: IRSFormDetail;
  editable: boolean;
  formType: 'lc' | 'sc' | 'rg';
  onApprove?: () => void;
  onReject?: () => void;
}

const IRSFormSections: React.FC<IRSFormSectionsProps> = ({
  formId,
  formDetail,
  editable,
  formType,
  onApprove,
  onReject,
}) => {
  const [value, setValue] = useState<string>('');

  const { data: sectionsData, isLoading: sectionsLoading } = useApiGet(
    ['irs-form-sections', formId],
    () => getIRSFormSections(formId),
    {
      enabled: !!formId,
      retry: 1,
    }
  );

  const sections = useMemo(() => sectionsData || [], [sectionsData]);

  // Compute the current tab value, ensuring it's always valid
  const currentTabValue = useMemo(() => {
    // If we have a value and it's valid (exists in sections or is "summary"), use it
    if (
      value &&
      (sections.some((s) => s.id === value) || value === 'summary')
    ) {
      return value;
    }
    // Otherwise, default to first section or "summary" if no sections
    return sections.length > 0 ? sections[0].id : 'summary';
  }, [value, sections]);

  useEffect(() => {
    // Set initial tab to first section if value is empty
    if (sections.length > 0 && !value) {
      setValue(sections[0].id);
    }
  }, [sections, value]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  if (sectionsLoading) {
    return <Box>Loading sections...</Box>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TabContext value={currentTabValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList
            onChange={handleChange}
            aria-label="IRS form sections"
            TabIndicatorProps={{
              sx: { height: '6px' },
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {sections.map((section) => (
              <Tab key={section.id} label={section.name} value={section.id} />
            ))}
            <Tab label="Summary" value="summary" />
          </TabList>
        </Box>
        {sections.map((section) => (
          <TabPanel key={section.id} value={section.id}>
            {/* Display dependency questions if any */}
            {section.dependency_questions &&
              section.dependency_questions.length > 0 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Information from Other Sections
                  </Typography>
                  {section.dependency_questions.map((depQuestion) => (
                    <Box key={depQuestion.question_id} sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {depQuestion.question_number || ''}.{' '}
                        {depQuestion.question_text}
                        {depQuestion.helper_text && (
                          <Tooltip
                            title={
                              <Typography sx={{ whiteSpace: 'pre-line' }}>
                                {depQuestion.helper_text}
                              </Typography>
                            }
                            arrow
                          >
                            <InfoIcon
                              sx={{
                                fontSize: 16,
                                color: 'primary.main',
                                ml: 0.5,
                                verticalAlign: 'middle',
                                cursor: 'help',
                              }}
                            />
                          </Tooltip>
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ ml: 2, color: 'text.secondary' }}
                      >
                        {depQuestion.decimal_answer !== null &&
                        depQuestion.decimal_answer !== undefined
                          ? `$${depQuestion.decimal_answer.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : depQuestion.integer_answer !== null &&
                              depQuestion.integer_answer !== undefined
                            ? depQuestion.integer_answer.toLocaleString()
                            : depQuestion.text_answer
                              ? depQuestion.text_answer
                              : depQuestion.boolean_answer !== null &&
                                  depQuestion.boolean_answer !== undefined
                                ? depQuestion.boolean_answer
                                  ? 'Yes'
                                  : 'No'
                                : depQuestion.date_answer
                                  ? new Date(
                                      depQuestion.date_answer
                                    ).toLocaleDateString()
                                  : depQuestion.document_id
                                    ? 'Document attached'
                                    : 'Not answered'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            <IRSFormSection
              formId={formId}
              sectionId={section.id}
              editable={editable}
              formType={formType}
              isActive={currentTabValue === section.id}
            />
          </TabPanel>
        ))}
        <TabPanel value="summary">
          <IRSFormSummary
            formDetail={formDetail}
            editable={editable}
            formType={formType}
            onApprove={onApprove}
            onReject={onReject}
            isActive={currentTabValue === 'summary'}
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default IRSFormSections;
